import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const BulkCreateResults = ({ result, title }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  // ✅ memoize rows first
  const rows = useMemo(() => {
    return Array.isArray(result?.results) ? result.results : [];
  }, [result?.results]);

  const summary = result?.summary;

  const failed = useMemo(
    () => rows.filter((r) => !r.success),
    [rows]
  );

  const created = useMemo(
    () => rows.filter((r) => r.success),
    [rows]
  );

  if (!summary && rows.length === 0) return null;

  const visibleFailed = showAll ? failed : failed.slice(0, 10);
  const visibleCreated = showAll ? created : created.slice(0, 10);

  return (
    <Stack spacing={1.5}>
      <Alert severity={failed.length ? 'warning' : 'success'}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">
            {title || t('massCreateCredentials.result.title', 'Results')} —{' '}
            {t('massCreateCredentials.result.created', 'Created')}:{' '}
            {summary?.created ?? created.length},{' '}
            {t('massCreateCredentials.result.failed', 'Failed')}:{' '}
            {summary?.failed ?? failed.length}
          </Typography>

          {failed.length > 0 && (
            <Chip
              size="small"
              label={t('massCreateCredentials.result.hasFailures', 'Has failures')}
              variant="outlined"
            />
          )}
        </Stack>
      </Alert>

      {/* Failed table */}
      {failed.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Box
            sx={{
              px: 2,
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle1">
              {t('massCreateCredentials.result.failedRows', 'Failed items')} ({failed.length})
            </Typography>
            {failed.length > 10 && (
              <Button size="small" onClick={() => setShowAll((v) => !v)}>
                {showAll ? t('common.showLess', 'Show less') : t('common.showAll', 'Show all')}
              </Button>
            )}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>{t('common.index', 'Index')}</TableCell>
                <TableCell>{t('common.email', 'Email')}</TableCell>
                <TableCell>{t('common.error', 'Error')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleFailed.map((r) => (
                <TableRow key={`fail-${r.index}`}>
                  <TableCell>{r.index}</TableCell>
                  <TableCell>{r.email || '—'}</TableCell>
                  <TableCell>{r.error || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Created table */}
      <Collapse in={created.length > 0}>
        <TableContainer component={Paper} variant="outlined">
          <Box
            sx={{
              px: 2,
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle1">
              {t('massCreateCredentials.result.createdRows', 'Created items')} ({created.length})
            </Typography>
            {created.length > 10 && failed.length === 0 && (
              <Button size="small" onClick={() => setShowAll((v) => !v)}>
                {showAll ? t('common.showLess', 'Show less') : t('common.showAll', 'Show all')}
              </Button>
            )}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>{t('common.index', 'Index')}</TableCell>
                <TableCell>{t('common.email', 'Email')}</TableCell>
                <TableCell width={220}>
                  {t('massCreateCredentials.result.code', 'Code')}
                </TableCell>
                <TableCell width={140}>
                  {t('massCreateCredentials.result.invalidated', 'Invalidated (previous codes)')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCreated.map((r) => (
                <TableRow key={`ok-${r.index}`}>
                  <TableCell>{r.index}</TableCell>
                  <TableCell>{r.email || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {r.code || '—'}
                  </TableCell>
                  <TableCell>{r.invalidated_previous ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Stack>
  );
};

export default BulkCreateResults;
